// src/portal/PortalReviewerMap.jsx
//
// The reviewer's map. THE SUBMISSION OVER IMAGERY, plus one MlDraw instance.
//
// ── WHY IMAGERY IS NOT OPTIONAL HERE ────────────────────────────────────────
//
// Decision 14. A reviewer is judging a setback or a surface conflict — claims
// about what is ACTUALLY ON THE GROUND — and a plan outline on a blank basemap
// cannot answer either. The submitter's map is vector-only because they are
// checking their own geometry; this one is not, because the reviewer is checking
// it against the world.
//
// Two layers of it, and the distinction matters:
//
//   THE ESRI BASE always renders. Tokenless, no entitlement, so the map is
//   never blank even when the org owns no imagery over this ground.
//
//   THE ENTITLED RASTER renders on top when route 6 resolved one. That is
//   AI Prime's own high-resolution product, resolved SERVER-SIDE by the same
//   resolveDatasetForGeometry call route 9 records as the markup witness — so
//   what the reviewer is looking at and what the audit says they were looking
//   at come from one resolution, not two.
//
//   null is a real answer and renders as one. datasetResolver's own contract:
//   "no visual imagery for this location" beats an index rendered as a
//   photograph.
//
// ── THE TILE SESSION CARRIES NO PROJECT ─────────────────────────────────────
//
// noProject: true. A plan review is not project-scoped — the same reason
// layerStaging's projectId became scopeId — and a reviewer who happens to have
// a project selected in the platform shell would otherwise send it to
// /tile/session, where authz-api VALIDATES it and answers 403 INVALID_PROJECT
// rather than falling back. The flag is part of the cache key, so this map's
// token and the platform's cannot be confused for one another.
//
// ── ONE MlDraw, ON ONE MAP ──────────────────────────────────────────────────
//
// ml-draw.js assigns constant source ids and uses string literals for its nine
// layer ids in BOTH addLayer and destroy(), with no constructor option to
// namespace them. Two instances on one map collide on addSource; two instances
// on two maps do not. This portal renders one map at a time — the submitter's
// or this one, never both — which is why the constraint is satisfied by
// construction rather than by care.
import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import MlDraw from "../lib/ml-draw/ml-draw.js";
import { getOrFetchTileToken, bindTileAuthHandler } from "../api/tileTokens";
import {
  ESRI_NEUTRAL_STYLE, fitToFeatures, addSubmissionLayers, addMarkupLayers,
} from "./portalMapStyle";
import { useFeatureSelection } from "./useFeatureSelection";
import { syncMarkupLabels } from "./portalMarkupLabels";

const IMAGERY_SOURCE = "portal-entitled-imagery";

/**
 * @param {object} p
 * @param {Array}  p.features
 * @param {{dataset_key: string, lod: string, acquisition_date: string}|null} p.imagery
 * @param {Function} [p.onDraw]
 * @param {number|null} [p.selectedOrdinal]   the feature the page considers selected
 * @param {Function|null} [p.onSelectFeature] called with an ordinal, or null
 *
 * ── SELECTION IS DORMANT UNTIL THE MARKUP FORM LANDS ───────────────────────
 *
 * PortalReviewPage does not pass the last two yet, so no click handler is
 * registered and nothing is highlighted -- exactly what it does today. That is
 * dormancy, not an inert control: nothing appears on screen offering an action
 * it cannot perform. The wiring lands now because the markup form needs a
 * feature picker and the alternative was a SECOND copy of it, and four of the
 * nine mutations that landed the tag control were against these lines.
 */
export default function PortalReviewerMap({
  features, imagery, markup = [], onDraw, selectedOrdinal = null, onSelectFeature = null,
  drawnConsumed = 0,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  // ── PLACEMENT IS A FOURTH PARTICIPANT THAT MlDraw CANNOT SEE ──────────────
  //
  // `drawing` is MlDraw's own flag and only _begin() sets it — startPolygon,
  // startRectangle, startCircle, startLine. There is NO point mode: `_click`
  // handles polygon, rectangle, circle and line and nothing else
  // (ml-draw.js:418-447), and `add()` sets the final feature WITHOUT setting
  // `drawing` (ml-draw.js:366-373).
  //
  // So placing a note is the portal's own click, and `drawing === false`
  // throughout it. A predicate that asks MlDraw whether it is busy answers NO
  // while the reviewer is mid-placement, and the click that places the pin is
  // ALSO read as a selection — §3.29's collision in a new costume, with the
  // arbitration looking at the wrong tool.
  const pendingPinRef = useRef(null);
  const placingRef = useRef(false);
  const [imageryError, setImageryError] = useState(null);
  const { registerClickHandlers } = useFeatureSelection({
    mapRef, features, selectedOrdinal, onSelectFeature,
    // SELECTION YIELDS WHILE A SHAPE IS BEING DRAWN. MlDraw's click handler is
    // registered FIRST (by addControl) and MapLibre fires every listener
    // regardless of preventDefault, so nothing downstream can protect a vertex
    // click from also being read as a selection. Asking is the only arbitration
    // available without editing ml-draw.js, which four other products share.
    //
    // TWO TOOLS, ASKED SEPARATELY. `drawing` covers MlDraw's four modes;
    // `placingRef` covers the one this component owns. Neither can answer for
    // the other, and dropping either arm is a mutation with a test.
    isSuspended: () => drawRef.current?.drawing === true || placingRef.current === true,
  });
  // The same stale-closure guard useFeatureSelection uses, for the same reason:
  // draw.onUpdate is assigned once, inside the load handler.
  const onDrawRef = useRef(onDraw);
  onDrawRef.current = onDraw;
  const [drawMode, setDrawMode] = useState(null);
  // Whether MlDraw is holding a shape. Set from onUpdate, which every path that
  // adds or removes one already goes through.
  const [hasGeom, setHasGeom] = useState(false);
  // MIRRORED DURING RENDER, NOT CLEARED IN THE HANDLER. The click that places a
  // pin also ends placement, and if the handler cleared this flag itself then
  // whether selection yielded would depend on REGISTRATION ORDER — the
  // listeners after it in the same dispatch would see `false` and select.
  // Assigning from state means the flag falls only once React commits, which is
  // after the whole dispatch, so every listener in that click sees the same
  // answer. §3.29 is about not relying on ordering; this is the same rule.
  placingRef.current = drawMode === "point";
  // WHICH FEATURE THE CURRENT SHAPE IS ABOUT. null means "not yet spoken for" —
  // either there is no shape, or it was drawn before a subject was chosen.
  const drawnAgainstRef = useRef(null);
  // Read inside draw.onUpdate, which is assigned once in the load handler — the
  // same stale-closure guard everything else on this map uses.
  const selectedOrdinalRef = useRef(selectedOrdinal);
  selectedOrdinalRef.current = selectedOrdinal;

  // ── WHY THE PIN IS A DOM MARKER AND NOT MlDRAW'S OWN RENDERING ───────────
  //
  // MlDraw draws its final feature with exactly two layers —
  // `ml-draw-final-fill` (fill) and `ml-draw-final-line` (line), ml-draw.js:121
  // and :131. Neither renders Point geometry. `_updateHandles` is no help
  // either: with vertex editing off it calls `_getPolygonCoords(feature)`,
  // which yields nothing for a Point, and clears the handles (:658-662).
  //
  // So `draw.add(point)` succeeds, `getAll()` returns it, `onDraw` receives it
  // and route 9 would store it — AND THE REVIEWER SEES NOTHING. The whole data
  // path works while the map stays empty, which is §3.31's symbol layer in a
  // different costume: a mechanism that reports success and draws nothing.
  //
  // Same answer as §3.31, for the same reason — a DOM marker owned here. It is
  // NOT syncMarkupLabels: that module renders a SAVED finding's note, and a pin
  // being placed has no note and no id yet. One mechanism, two distinct things.
  function syncPendingPin(feature) {
    const map = mapRef.current;
    if (!map) return;
    const at = feature?.geometry?.type === "Point" ? feature.geometry.coordinates : null;
    if (!at) {
      pendingPinRef.current?.remove();
      pendingPinRef.current = null;
      return;
    }
    if (pendingPinRef.current) { pendingPinRef.current.setLngLat(at); return; }
    const el = document.createElement("div");
    el.dataset.testid = "pending-pin";
    // FIFTH PARTICIPANT, DECLINED. Four things already read this map's click.
    el.style.pointerEvents = "none";
    el.style.cssText += [
      "width:13px", "height:13px", "border-radius:50%",
      // The markup accent, so a pin reads as a finding rather than a handle.
      "background:var(--geom-markup-rgb, #f472b6)", "border:2px solid #ffffff",
      "box-shadow:0 0 0 1px #0C0E10",
    ].join(";");
    pendingPinRef.current = new maplibregl.Marker({ element: el }).setLngLat(at).addTo(map);
  }

  /** Starting a draw claims the current selection as its subject, or nothing. */
  function claimDrawSubject() {
    drawnAgainstRef.current = Number.isInteger(selectedOrdinalRef.current)
      ? selectedOrdinalRef.current : null;
  }

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: ESRI_NEUTRAL_STYLE,
      center: [-102, 32],
      zoom: 7,
      attributionControl: false,
    });
    map.getCanvas().style.background = "var(--map-backdrop-rgb, #0C0E10)";
    mapRef.current = map;
    bindTileAuthHandler(map);

    map.on("load", () => {
      // ONE INSTANCE, created inside on("load") and torn down in the cleanup —
      // PlatformCoverageAoiMap.jsx:265-272 and :460-470, which is the pattern.
      const draw = new MlDraw();
      // ── READ getAll(), DO NOT TRUST THE ARGUMENT ────────────────────────
      //
      // MlDraw calls onUpdate(feature) on _commit and onUpdate() with NO
      // ARGUMENT on delete() -- and ALSO on every vertex drag, transform-handle
      // move and add(). So "undefined means cleared" is FALSE: it means
      // "something changed, ask me". A handler trusting the argument would
      // report the shape as cleared halfway through the reviewer editing it,
      // silently, and it would look like the map losing their work.
      //
      // getAll() returns the final FeatureCollection either way, so it is the
      // only reading that is right for all six call sites.
      draw.onUpdate = () => {
        const fc = draw.getAll();
        const feature = fc?.features?.[0] ?? null;
        // THE PIN FOLLOWS MlDraw, IT DOES NOT SHADOW IT. Clear, Escape, a
        // change of subject and a delete all reach MlDraw and all come back
        // through here, so the pin has no lifecycle of its own to get wrong.
        syncPendingPin(feature);
        setHasGeom(Boolean(feature));
        // Cleared shape, cleared subject. Claiming one is claimDrawSubject's
        // job and happens when the draw STARTS -- an in-progress shape is about
        // something too, and a reviewer who starts drawing on #0 and switches to
        // #1 has abandoned it just as surely as if they had finished it.
        if (!feature) drawnAgainstRef.current = null;
        onDrawRef.current?.(feature);
      };
      map.addControl(draw);
      drawRef.current = draw;

      addSubmissionLayers(map, features);
      // AFTER the submission, so a finding always draws on top of the shape it
      // is about. A remark hidden under its subject is not a remark.
      addMarkupLayers(map, markup);
      syncMarkupLabels(map, markup);
      fitToFeatures(map, features);
      // AFTER the layers exist: the handlers are layer-scoped, so registering
      // them earlier attaches them to layers that are not there yet.
      registerClickHandlers(map);

      // ── RIGHT-CLICK CLOSES THE SHAPE. THE CONSUMER BINDS THIS. ──────────
      //
      // MlDraw does NOT close a shape by itself. `_dblclick()` is an empty
      // method whose body reads "intentionally disabled — we use right-click to
      // close polygon/line" (ml-draw.js:480), and the only `contextmenu`
      // binding inside the class is for VERTEX EDITING. Every consumer that can
      // actually finish a shape binds this itself — MapView.jsx:666-674 is the
      // pattern this follows.
      //
      // This shipped WITHOUT it, with a hint that said "double-click to finish".
      // Points could be placed and nothing could ever complete. The cause was
      // reading that `map.on("dblclick", …)` was REGISTERED and inferring what
      // it did, instead of reading the body — the same mistake as checking a
      // constant's export instead of calling the function that uses it.
      // ── PLACEMENT: ONE CLICK, AND IT IS THIS COMPONENT'S, NOT MlDraw'S ──
      //
      // Registered AFTER registerClickHandlers so the selection listeners in
      // this dispatch have already yielded on `placingRef` before the pin
      // lands. That ordering is a convenience, not the mechanism — the flag
      // falls on React's commit rather than in this handler, so the arbitration
      // holds whichever order these were registered in.
      //
      // ONE SHOT. A mode that stayed armed would put a second pin on the next
      // click, and the geometry a finding carries is single — `getAll()` is
      // read as `features[0]`, so a second point would silently replace the
      // first while looking like it added one.
      //
      // NOT REGISTERED ON A READ-ONLY MAP. `placingRef` cannot be true without
      // the toolbar, which cannot render without `onDraw`, so the body is inert
      // there — but "inert body" is not the rule this file settled on. The rule
      // next door is that a dormant feature does not register live handlers at
      // all, and it was written because reasoning about an inert body is what
      // hid the last one. Guarded at load, the way registerClickHandlers is.
      if (onDrawRef.current) map.on("click", (e) => {
        if (!placingRef.current) return;
        const d = drawRef.current;
        if (!d) return;
        e.preventDefault?.();
        d.add({
          type: "Feature", properties: {},
          geometry: { type: "Point", coordinates: [e.lngLat.lng, e.lngLat.lat] },
        });
        setDrawMode(null);
      });

      map.on("contextmenu", (e) => {
        const d = drawRef.current;
        if (!d || !d.drawing) return;
        const at = [e.lngLat.lng, e.lngLat.lat];
        if (d.mode === "polygon") d.completePolygon?.(at);
        else if (d.mode === "line") d.completeLine?.(at);
        setDrawMode(null);
      });
    });

    return () => {
      pendingPinRef.current?.remove();
      pendingPinRef.current = null;
      if (mapRef.current && drawRef.current) mapRef.current.removeControl(drawRef.current);
      mapRef.current?.remove();
      mapRef.current = null;
      drawRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The entitled raster, when route 6 resolved one.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !imagery?.dataset_key) return;
    let cancelled = false;

    (async () => {
      try {
        const { ck } = await getOrFetchTileToken(imagery.dataset_key, {
          // THE WHOLE REASON THE OPT-OUT EXISTS. See the header.
          noProject: true,
        });
        if (cancelled || !map.isStyleLoaded()) return;
        if (map.getSource(IMAGERY_SOURCE)) return;

        map.addSource(IMAGERY_SOURCE, {
          type: "raster",
          tiles: [`/tiles/WebMercatorQuad/{z}/{x}/{y}?dataset=${encodeURIComponent(imagery.dataset_key)}&ck=${encodeURIComponent(ck)}`],
          tileSize: 256,
        });
        // Above the Esri base, below the submission overlay — the plan must
        // stay legible over whatever is underneath it.
        map.addLayer({ id: IMAGERY_SOURCE, type: "raster", source: IMAGERY_SOURCE },
                     map.getLayer("portal-submission-fill") ? "portal-submission-fill" : undefined);
      } catch (e) {
        // A failed tile session is NOT a failed map. The Esri base is still
        // there, so the reviewer can still work — they just need to know they
        // are not looking at the entitled product.
        if (!cancelled) setImageryError(String(e?.message ?? e));
      }
    })();

    return () => { cancelled = true; };
  }, [imagery?.dataset_key]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    addSubmissionLayers(map, features);
    addMarkupLayers(map, markup);
    syncMarkupLabels(map, markup);
    fitToFeatures(map, features);
  }, [features, markup]);

  // ── GEOMETRY NOT ATTACHED TO A FINDING IS DISCARDED ─────────────────────
  //
  // A shape drawn about feature #0 and left there while the reviewer selects #1
  // is not a finding about #1 -- it is not a finding at all. Carrying it across
  // would let a reviewer attach an area they drew about something else, which
  // is a wrong assertion in a record the submitter reads.
  //
  // delete() fires onUpdate(), so the page's drawn geometry clears through the
  // same path a manual Clear takes. Nothing special-cases this.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;

    // ── A CHANGE OF SUBJECT, NOT THE CHOOSING OF ONE ──────────────────────
    //
    // This fired on ANY change of selectedOrdinal, including null -> N, so
    // choosing the subject for the first time was indistinguishable from
    // changing it -- and DRAW-FIRST was impossible: circle the encroachment,
    // click the feature it is about, and the shape was deleted on the way in.
    //
    // Draw-first is the permit-review gesture and is legitimised rather than
    // forbidden. What the rule is actually for is the OTHER order: a shape
    // drawn about #0 must not follow the reviewer to #1, because it is not a
    // finding about #1.
    //
    // So the geometry remembers which ordinal it was drawn against. Arriving
    // from `null` ADOPTS the new selection; moving between two features
    // discards.
    const drawnAgainst = drawnAgainstRef.current;
    const arrivingFromNothing = drawnAgainst === null;

    if (arrivingFromNothing) {
      // Adopt: the shape is now about whatever was just chosen. Recorded even
      // when nothing is selected yet, so the next selection adopts rather than
      // discards.
      drawnAgainstRef.current = Number.isInteger(selectedOrdinal) ? selectedOrdinal : null;
      return;
    }
    if (drawnAgainst === selectedOrdinal) return;   // same subject, nothing to do

    draw.stopDrawing?.();
    draw.delete?.();
    drawnAgainstRef.current = Number.isInteger(selectedOrdinal) ? selectedOrdinal : null;
    setDrawMode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrdinal]);

  // ── THE WRITTEN FINDING'S SHAPE IS NOT WORKING STATE ANY MORE ───────────
  //
  // DECISION: clear on write. The alternative — keep the shape as working state
  // until the reviewer clears it — was considered because a pass usually
  // produces several findings and a warm tool sounds like the friendlier
  // answer. It is not, for three reasons that are about the reviewer's task:
  //
  //   SEVERAL FINDINGS PER PASS ARE NOT SEVERAL FINDINGS ON ONE SHAPE. The
  //   geometry is the thing that distinguishes them — a setback here, a missing
  //   survey there. Keeping the LAST shape warm does nothing to help draw the
  //   NEXT one; it only leaves the old one lying where it can be reused.
  //
  //   ROUTE 9 WOULD ACCEPT THE REUSE SILENTLY. There is no idempotency key and
  //   no uniqueness on geometry, so with the shape still held, the default next
  //   action — pick a type, type a note, Record — writes a SECOND finding
  //   carrying the first one's geometry, with nothing on screen saying the
  //   first consumed it. Making persistence safe would mean adding a warning
  //   whose only job is to guard a state we chose to create.
  //
  //   AND THE FINDING IS ALREADY ON THE MAP. Route 6 is re-read, and the saved
  //   markup renders — as an outline, or as a label for a Point (§3.31). Under
  //   persistence the reviewer sees the same finding twice, once live-editable
  //   and once not.
  //
  // What stays warm is what a reviewer actually reuses: the form stays open on
  // the same feature (PortalFindingForm says so in its own header) and the
  // toolbar is one click away. This matches QcPolygonDrawer, which deletes and
  // stops on commit and lets the SAVED record render afterwards.
  //
  // ── THROUGH MlDraw, NOT AROUND IT ───────────────────────────────────────
  //
  // delete() fires onUpdate(), which is the one path that removes the pin,
  // drops `hasGeom`, resets the SUBJECT CLAIM to null and reports onDraw(null).
  // Reaching into any of those directly would leave the others behind — which
  // is how a pin outlived its geometry once already.
  const lastConsumed = useRef(drawnConsumed);
  useEffect(() => {
    // Not on mount. The initial value is not a command.
    if (drawnConsumed === lastConsumed.current) return;
    lastConsumed.current = drawnConsumed;
    const draw = drawRef.current;
    if (!draw) return;
    draw.stopDrawing?.();
    draw.delete?.();
    setDrawMode(null);
  }, [drawnConsumed]);

  // Escape cancels a draw in progress. Bound on the document rather than the
  // canvas, because the reviewer may have focus in the finding form beside it.
  useEffect(() => {
    if (!onDraw) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      drawRef.current?.stopDrawing?.();
      setDrawMode(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDraw]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      {/* ── THE DRAW TOOLBAR ────────────────────────────────────────────────
          MlDraw.onAdd() returns an EMPTY div (ml-draw.js:256) — it ships no UI
          of its own, and nothing called startPolygon/startLine, so `mode` was
          null forever and no click could begin a shape. Every other consumer in
          the tree pairs it with a toolbar (AoiToolbar.jsx:154-160,
          QcPolygonDrawer.jsx:204, PlatformCoverageAoiMap.jsx:173); this is the
          portal's.

          AREA AND LINE ONLY. A finding is an area or a corridor.
          startRectangle and startCircle exist and are AOI tools — a circled
          radius is not a thing a reviewer asserts about a pad plan.

          Rendered only when somebody is listening. Without onDraw this is the
          read-only reviewer map, and a toolbar that produced geometry nothing
          received is the inert-control failure this release keeps hitting. */}
      {onDraw && (
        <div className="absolute right-2 top-2 flex gap-1" data-testid="draw-toolbar">
          <button
            type="button"
            data-testid="draw-polygon"
            aria-pressed={drawMode === "polygon"}
            onClick={() => { claimDrawSubject(); drawRef.current?.startPolygon?.(); setDrawMode("polygon"); }}
            className="rounded border border-bg-border bg-black/60 px-2 py-1 text-[11px]"
          >
            Draw area
          </button>
          <button
            type="button"
            data-testid="draw-line"
            aria-pressed={drawMode === "line"}
            onClick={() => { claimDrawSubject(); drawRef.current?.startLine?.(); setDrawMode("line"); }}
            className="rounded border border-bg-border bg-black/60 px-2 py-1 text-[11px]"
          >
            Draw line
          </button>
          <button
            type="button"
            data-testid="draw-point"
            aria-pressed={drawMode === "point"}
            onClick={() => {
              // NO MlDraw START CALL. There is no point mode to start; the
              // click below is this component's. stopDrawing() first so that
              // arming placement while a polygon is half-drawn does not leave
              // two tools armed at once.
              claimDrawSubject();
              drawRef.current?.stopDrawing?.();
              setDrawMode("point");
            }}
            className="rounded border border-bg-border bg-black/60 px-2 py-1 text-[11px]"
          >
            Place note
          </button>
          <button
            type="button"
            data-testid="draw-clear"
            onClick={() => { drawRef.current?.stopDrawing?.(); drawRef.current?.delete?.(); setDrawMode(null); }}
            className="rounded border border-bg-border bg-black/60 px-2 py-1 text-[11px]"
          >
            Clear
          </button>
        </div>
      )}

      {drawMode && (
        <p className="pointer-events-none absolute right-2 top-10 rounded bg-black/60 px-2 py-1 text-[11px] text-fg-secondary"
           data-testid="draw-hint">
          {/* THE HINT MUST DESCRIBE THE GESTURE THAT EXISTS. §3.28 shipped
              "double-click to finish" against an empty `_dblclick`, and a
              reviewer followed it and nothing happened. A one-shot pin has no
              finishing gesture at all, so telling them to right-click would be
              the same defect with the tools swapped. */}
          {drawMode === "point"
            ? "Click once to place the note. Escape cancels."
            : "Click to place points, right-click to finish. Escape cancels."}
        </p>
      )}
      {/* ── THE PROMPT GOES WHERE THEIR EYES ARE ───────────────────────────
          Route 9 requires `feature_id`, so a shape with no subject cannot be
          written. The form says so — but the form is BELOW the map, and a
          reviewer who has just placed a pin is looking at the pin. The reported
          symptom was "there is no input to type the text": the instruction
          existed and was nowhere near the thing that prompted the question.

          Not specific to placement. Order A is the permit-review gesture for
          areas and lines too (§3.29), and all three have had the same silent
          gap. */}
      {!drawMode && hasGeom && !Number.isInteger(selectedOrdinal) && (
        <p className="pointer-events-none absolute left-2 top-2 rounded bg-black/70 px-2 py-1 text-[11px]"
           data-testid="draw-subject-prompt">
          Now click the shape on the plan this is about.
        </p>
      )}

      {/* WHAT THEY ARE LOOKING AT, SAID OUT LOUD. A reviewer judging a setback
          needs to know whether this is the entitled capture or a public
          basemap, and the capture date is the fact the markup witness records
          against their finding. */}
      <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-2 py-1 text-[11px] text-fg-secondary">
        {imageryError
          ? `Entitled imagery unavailable (${imageryError}) — showing the public basemap.`
          : imagery?.dataset_key
            ? `${imagery.dataset_key} · ${imagery.lod} · captured ${String(imagery.acquisition_date).slice(0, 10)}`
            : "No entitled imagery covers this plan — showing the public basemap."}
      </div>
    </div>
  );
}
